/**
 * syc-card-builder.js
 *
 * JS counterpart to snippets/syc-product-card.liquid.
 * Used wherever product cards must be built client-side (e.g. the wishlist
 * carousel, which is fully JS-rendered from a PHP API response).
 *
 * The Liquid snippet remains the single source of truth for server-rendered
 * contexts. This file mirrors its markup and logic so both stay in sync.
 *
 * Setup (in a Liquid section before this script loads):
 *   <script>
 *     window.SycCardBuilder.configure({
 *       moneyFormat:  {{ shop.money_format | json }},
 *       discountCode: {{ settings.active_discount_code | default: '' | json }},
 *       discountPct:  {{ settings.active_discount_percent | default: 0 | json }}
 *     });
 *   </script>
 */

(function() {
    'use strict';

    var _cfg = {
        moneyFormat: '${{amount}}',
        discountCode: '',
        discountPct: 0
    };

    // ── Mirrors product.price | divided_by: 100 | money ───────────────────
    // storeCents = rawCents / 100  (caller applies the store-specific correction)
    function formatMoney(storeCents) {
        if (storeCents == null || storeCents === '') return '';
        var cents = Math.round(Number(storeCents));
        if (!isFinite(cents)) return '';
        var fmt = _cfg.moneyFormat;
        var dollars = cents / 100;
        var value;
        if (fmt.indexOf('amount_no_decimals_with_comma_separators') !== -1) {
            value = Math.floor(dollars).toString()
                .replace(/\B(?=(\d{3})+(?!\d))/g, '.');
        } else if (fmt.indexOf('amount_with_comma_separators') !== -1) {
            value = dollars.toFixed(2).replace('.', ',')
                .replace(/\B(?=(\d{3})+(?!\d))/g, '.');
        } else if (fmt.indexOf('amount_no_decimals') !== -1) {
            value = Math.floor(dollars).toString()
                .replace(/\B(?=(\d{3})+(?!\d))/g, ',');
        } else {
            value = dollars.toFixed(2)
                .replace(/\B(?=(\d{3})+(?!\d))/g, ',');
        }
        return fmt.replace(/\{\{\s*\w+\s*\}\}/, value);
    }

    function esc(s) {
        return String(s == null ? '' : s)
            .replace(/&/g, '&amp;').replace(/</g, '&lt;')
            .replace(/>/g, '&gt;').replace(/"/g, '&quot;');
    }

    // ── Build a syc-card <a> element (mirrors syc-product-card.liquid) ────
    // p        — Shopify product object from /products/<handle>.js
    // opts     — {
    //     id            : product id string (for data attrs / remove buttons)
    //     removeButton  : boolean — add the wishlist-page X remove button
    //     roleListitem  : boolean — add role="listitem" to the <a>
    //   }
    function buildCard(p, opts) {
        opts = opts || {};
        var id = String(opts.id != null ? opts.id : (p.id || ''));

        var TITLE_MAX = 75;
        var title = p.title || 'Product';
        if (title.length > TITLE_MAX) title = title.slice(0, TITLE_MAX - 1) + '…';

        // opts.brand is injected by Wishlist.liquid from the localStorage cache
        // (populated by wishlist-button.liquid at render time from the Liquid metafield).
        var brand = (opts.brand !== undefined && opts.brand !== null && opts.brand !== '')
          ? String(opts.brand)
          : ((p.metafields && p.metafields.custom && p.metafields.custom.product_brand) || '');

        // Images
        var featured = p.featured_image || (p.images && p.images[0]) || '';
        var secondary = (p.images && p.images[1]) || '';
        if (featured && featured.indexOf && featured.indexOf('//') === 0) featured = 'https:' + featured;
        if (secondary && secondary.indexOf && secondary.indexOf('//') === 0) secondary = 'https:' + secondary;

        // Pass raw Shopify cents directly — mirrors: product.price | money
        // formatMoney handles the /100 conversion internally.
        var p_price = Number(p.price);
        var p_compare = Number(p.compare_at_price);
        var priceStr = formatMoney(p_price);
        var compareStr = p.compare_at_price > p.price ? formatMoney(p_compare) : '';

        var catalogLiveTs = Number(opts.catalogLiveTs) || 0;
        var catalogUpcoming = catalogLiveTs > 0 && Math.floor(Date.now() / 1000) < catalogLiveTs;

        var discountActive = !!(_cfg.discountCode && _cfg.discountPct > 0);
        var priceHtml = '', badgeHtml = '';

        if (!catalogUpcoming) {
            if (compareStr) {
                priceHtml =
                    '<span class="syc-card__price--compare">' + esc(compareStr) + '</span>' +
                    '<span class="syc-card__price--sale">' + esc(priceStr) + '</span>';
            } else if (discountActive) {
                var discounted = p_price * (100 - _cfg.discountPct) / 100;
                priceHtml =
                    '<span class="syc-card__price--compare">' + esc(priceStr) + '</span>' +
                    '<span class="syc-card__price--sale">' + esc(formatMoney(discounted)) + '</span>';
            } else {
                priceHtml = '<span>' + esc(priceStr) + '</span>';
            }
            if (discountActive) {
                badgeHtml = '<p class="syc-card__discount-badge">Discount Applied: ' + esc(_cfg.discountCode) + '</p>';
            }
        }

        var hasSecondary = !!secondary;
        var mediaCls = 'syc-card__media' + (hasSecondary ? ' syc-card__media--has-secondary' : '');

        var imgPrimary = featured ?
            '<img class="syc-card__img syc-card__img--primary" src="' + esc(featured) + '" alt="' + esc(p.title || '') + '" loading="lazy">' :
            '';
        var imgSecondary = hasSecondary ?
            '<img class="syc-card__img syc-card__img--secondary" src="' + esc(secondary) + '" alt="" loading="lazy" aria-hidden="true">' :
            '';

        var brandHtml = brand ?
            '<p class="syc-card__brand">' + esc(brand) + '</p>' :
            '';

        var removeBtn = opts.removeButton ?
            '<button type="button" class="wl-remove" data-remove-product="' + esc(id) + '" aria-label="Remove from wishlist">' +
            '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 4l16 16M20 4L4 20" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>' +
            '</button>' :
            '';

        var aAttrs = 'class="syc-card" href="/products/' + esc(p.handle) + '"' +
            (opts.roleListitem ? ' role="listitem"' : '') +
            (id ? ' data-product-id="' + esc(id) + '"' : '');

        var priceSection;
        if (catalogUpcoming) {
            var secsLeft = catalogLiveTs - Math.floor(Date.now() / 1000);
            var daysLeft = Math.max(0, Math.ceil(secsLeft / 86400));
            var countdownMsg = daysLeft === 0 ? 'Price revealed today'
                : daysLeft === 1 ? 'Price revealed tomorrow'
                : 'Price revealed in ' + daysLeft + ' days';
            priceSection = '<p class="syc-card__discount-badge">' + esc(countdownMsg) + '</p>';
        } else {
            priceSection = '<p class="syc-card__price">' + priceHtml + '</p>' + badgeHtml;
        }

        return '<a ' + aAttrs + '>' +
            removeBtn +
            '<div class="' + mediaCls + '">' + imgPrimary + imgSecondary + '</div>' +
            '<div class="syc-card__content">' +
            brandHtml +
            '<p class="syc-card__title">' + esc(title) + '</p>' +
            priceSection +
            '</div>' +
            '</a>';
    }

    // ── Collection card — full catalog-card layout (mirrors catalog-card snippet) ──
    // Used by the wishlist collections carousel.
    function buildCollectionCard(id, c) {
        var now = Math.floor(Date.now() / 1000);
        var openTs  = Number(c.catalogLiveTs) || 0;
        var closeTs = Number(c.closeTs) || 0;

        var status = '';
        if (openTs > 0 && closeTs > 0 && now < closeTs) {
            status = now >= openTs ? 'live' : 'upcoming';
        }

        function fmtDate(ts) {
            var d = new Date(ts * 1000);
            return (d.getMonth() + 1) + '/' + d.getDate();
        }

        var badgeHtml = status
            ? '<span class="catalog-card__badge catalog-card__badge--' + status + '">' + (status === 'live' ? 'OPEN' : 'UPCOMING') + '</span>'
            : '';
        var dateText = status === 'live'     ? 'Closing ' + fmtDate(closeTs)
                     : status === 'upcoming' ? 'Opens '   + fmtDate(openTs)
                     : '';
        var dateHtml  = dateText         ? '<span class="catalog-card__date">'  + esc(dateText)             + '</span>' : '';
        var countHtml = c.productCount > 0 ? '<p class="catalog-card__count">'  + esc(String(c.productCount)) + ' products</p>' : '';

        var img = c.imageUrl
            ? '<img class="catalog-card__img" src="' + esc(c.imageUrl) + '" alt="' + esc(c.title) + '" loading="lazy">'
            : '<div class="catalog-card__img catalog-card__img--placeholder" aria-hidden="true"></div>';

        var removeBtn = '<button type="button" class="wl-remove" data-remove-collection="' + esc(id) + '" aria-label="Remove from wishlist">' +
            '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 4l16 16M20 4L4 20" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>' +
            '</button>';

        return '<a class="catalog-card" href="/collections/' + esc(c.handle) + '" role="listitem" data-collection-id="' + esc(id) + '">' +
            removeBtn +
            '<div class="catalog-card__media">' + img + '</div>' +
            '<div class="catalog-card__top">' +
            '<div class="catalog-card__meta">' + badgeHtml + dateHtml + '</div>' +
            '<p class="catalog-card__title">' + esc(c.title) + '</p>' +
            countHtml +
            '</div>' +
            '</a>';
    }

    window.SycCardBuilder = {
        configure: function(cfg) {
            if (!cfg) return;
            if (cfg.moneyFormat != null) _cfg.moneyFormat = cfg.moneyFormat;
            if (cfg.discountCode != null) _cfg.discountCode = cfg.discountCode;
            if (cfg.discountPct != null) _cfg.discountPct = Number(cfg.discountPct);
        },
        formatMoney: formatMoney,
        buildCard: buildCard,
        buildCollectionCard: buildCollectionCard
    };
})();